import {
    Divider,
    Flex,
    Stack,
    Text,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    Link,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useTranslation } from "react-i18next";
import EmailLink from "components/links/email-link";
import { hasAvailableWaitListOffers, isBookingCanceled, isWaitList } from "utils/booking";

interface IBookingOverviewProps {
    disableOverview: boolean;
}

function BookingOverview({ disableOverview: disabledState }: IBookingOverviewProps) {
    const { data: settings } = useExternalBookingSettings();
    const { data: webBooking } = useWebBooking();
    const { t } = useTranslation();

    if (!webBooking) return null;

    const guests = webBooking.guests;

    const isWaitListBooking = isWaitList(webBooking);
    const hasAvailableOffers = hasAvailableWaitListOffers(webBooking);

    const children = webBooking.guestsChildren ?? 0;
    const showWaitListSummary = isWaitListBooking && !hasAvailableOffers;
    const showChildrenBreakdown = settings?.unitsMetaData.activateWebChildren && children > 0;
    const adults = Math.max(guests - children, 0);
    const guestBreakdownText = showChildrenBreakdown
        ? `(${adults} ${t("adults").toLowerCase()}, ${children} ${t("children").toLowerCase()})`
        : undefined;

    /**
     * Formats the booking time range
     * @returns Start time or range in format "HH.mm" or "HH.mm-HH.mm" (e.g., "18.00" / "18.00-22.00")
     */
    const getFormattedTime = () => {
        const startTime = webBooking.start.format("HH.mm");
        const endTime = webBooking.displayEndTime().format("HH.mm");
        return settings?.hideEndTimeForGuest ? startTime : `${startTime}-${endTime}`;
    };

    /**
     * Formats the booking date
     * @returns Date in format "MMM D" (e.g., "Dec 17")
     */
    const getFormattedDate = () => {
        const formatted = webBooking.start.format("MMM D");
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const formattedDataRow = (value: string, text: string, secondaryText?: string) => (
        <Flex width="fit-content" direction="column" gap={ThemeSpaceVariable.Medium}>
            <Stack gap="0px" width="fit-content">
                <Flex gap={ThemeSpaceVariable.Small} width="fit-content">
                    <Text fontWeight="700">{value}</Text>
                    <Text
                        fontWeight="400"
                        color="inherit"
                        opacity={0.65} // Due to lack of dark theme variables that are subdued we use this hack
                    >
                        {text}
                    </Text>
                </Flex>
                {secondaryText && (
                    <Text
                        fontWeight="400"
                        color="inherit"
                        opacity={0.65} // Due to lack of dark theme variables that are subdued we use this hack
                    >
                        {secondaryText}
                    </Text>
                )}
            </Stack>
            <Divider borderColor="inherit" />
        </Flex>
    );

    const isInquiry = webBooking.anyNonCompleteInquirySubStatus();

    const href = `tel:${settings?.phoneNumber}`;

    const startTimes = webBooking.articles
        .filter((art) => art.isTable)
        .sort((a, b) => a.start.valueOf() - b.start.valueOf())
        .map((time) => time.start.format("HH:mm"))
        .join(", ");

    return (
        <Flex direction="column" gap={ThemeSpaceVariable.Medium}>
            {isBookingCanceled(webBooking) && (
                <Text fontWeight="500" fontSize={ThemeFontSizeVariable.Large}>
                    {isInquiry
                        ? t("bookingRequest.requestRemoved")
                        : isWaitListBooking
                          ? t("waitList.waitListRemoved")
                          : t("bookingRemoved")}
                </Text>
            )}
            <Flex gap={ThemeSpaceVariable.Small} opacity={disabledState ? 0.48 : 1}>
                <Stack gap={ThemeSpaceVariable.Medium}>
                    {formattedDataRow(
                        guests + "",
                        guests === 1 ? t("person") : t("persons"),
                        guestBreakdownText
                    )}
                    {formattedDataRow(getFormattedDate(), t("time.datestring"))}
                    {showWaitListSummary &&
                        formattedDataRow(
                            startTimes,
                            webBooking.articles.length === 1 ? t("startTime") : t("startTimes")
                        )}
                    {!showWaitListSummary && formattedDataRow(getFormattedTime(), t("time.time"))}
                    {!showWaitListSummary &&
                        formattedDataRow(
                            webBooking.globalBookingNumber,
                            t("summary.globalBookingNumber")
                        )}
                    <Flex direction="column" gap={"0px"}>
                        {settings && settings.address && <Text>{settings.address}</Text>}
                        {settings && settings.phoneNumber && (
                            <Link href={href} textDecoration="none" color="inherit">
                                {settings.phoneNumber}
                            </Link>
                        )}
                        {settings && settings.email && (
                            <EmailLink
                                email={settings.email}
                                textDecoration="none"
                                color="inherit"
                            />
                        )}
                    </Flex>
                </Stack>
            </Flex>
        </Flex>
    );
}

export default BookingOverview;
